import { useState } from 'react';
import { Wand2, FlaskConical, BookOpen, Sparkles, Cookie, Trophy } from 'lucide-react';

interface ProductImageProps {
  src?: string;
  fallbackSrc?: string;
  alt: string;
  category: string;
  /** Used as a stable seed for the placeholder color/rotation. */
  sku: string;
  className?: string;
  loading?: 'lazy' | 'eager';
}

interface CategoryStyle {
  Icon: typeof Wand2;
  /** Two parchment-friendly stops; `from` is the deeper tone. */
  from: string;
  to: string;
  /** Icon stroke color. */
  ink: string;
}

const CATEGORY_STYLES: Record<string, CategoryStyle> = {
  Wandcraft:     { Icon: Wand2,         from: '#d8c7a3', to: '#f3eada', ink: '#5b3b16' },
  Apothecary:    { Icon: FlaskConical,  from: '#bcd4c0', to: '#eaf3ec', ink: '#244a2c' },
  Quidditch:     { Icon: Trophy,        from: '#cfb87d', to: '#f3e7c2', ink: '#5b3a05' },
  Familiars:     { Icon: Sparkles,      from: '#c8b3d4', to: '#ece4f3', ink: '#3a1d57' },
  Confectionery: { Icon: Cookie,        from: '#e6b8a8', to: '#f7e2d4', ink: '#5e2618' },
  'Books & Scrolls': { Icon: BookOpen,  from: '#bcb89c', to: '#ecead4', ink: '#3a3415' },
};

const FALLBACK: CategoryStyle = {
  Icon: Sparkles,
  from: '#cfc5b0',
  to: '#efe9d9',
  ink: '#3a2f17',
};

/** Stable 0–360 rotation seeded by SKU so each tile feels distinct. */
function seedRotation(sku: string): number {
  let h = 0;
  for (let i = 0; i < sku.length; i++) h = (h * 31 + sku.charCodeAt(i)) | 0;
  return ((h % 30) + 30) % 30 - 15; // -15..+15 deg
}

export function ProductImage({
  src,
  fallbackSrc,
  alt,
  category,
  sku,
  className,
  loading = 'lazy',
}: ProductImageProps) {
  // Pick a single URL: prefer the explicit `src`; if absent, the SKU-based
  // `fallbackSrc`. We don't chain src → fallbackSrc because they're usually
  // identical (the seed sets image_url = `/images/<sku>.png`), and React
  // skips re-fetch when the new src is the same string, so the second leg
  // never fires onError and the placeholder never renders.
  const url = src || fallbackSrc || null;
  const [errored, setErrored] = useState(false);

  if (url && !errored) {
    return (
      <img
        src={url}
        alt={alt}
        loading={loading}
        className={className}
        onError={() => setErrored(true)}
      />
    );
  }

  const style = CATEGORY_STYLES[category] ?? FALLBACK;
  const { Icon, from, to, ink } = style;
  const rotation = seedRotation(sku);

  return (
    <div
      className={className}
      role="img"
      aria-label={alt}
      style={{
        background: `linear-gradient(135deg, ${from}, ${to})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Icon
        size="48%"
        strokeWidth={1.25}
        color={ink}
        style={{
          opacity: 0.85,
          transform: `rotate(${rotation}deg)`,
          filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.08))',
        }}
        aria-hidden="true"
      />
    </div>
  );
}
